import axios from "axios";
import { APISchedulingUrl, APIUrlToken, isValidEmail } from "../../../Utils/Utils";

/**
 * Reports scheduling API client.
 *
 * POST {REACT_APP_SCHEDULING_API_URL}/schedules
 *   query:   user_id, user_email
 *   headers: X-API-Key (APIUrlToken, same as logEvent)
 *   body:    name, frequency, start_at, timezone, is_active, payload,
 *            job_buffer_time, job_type
 *
 * Datetime contract: start_at is sent as local wall-clock "YYYY-MM-DDTHH:mm:ss"
 * with NO timezone suffix; the zone travels separately in `timezone` (IANA).
 */

const ATLAS_JOB_TYPE = "atlas_research";
const ATLAS_JOB_BUFFER_TIME = 600;

// "YYYY-MM-DDTHH:mm" (datetime-local) -> "YYYY-MM-DDTHH:mm:ss" (no tz/Z).
const toApiDatetime = (local) => {
  if (!local) return local;
  return local.length === 16 ? `${local}:00` : local;
};

// Comma-separated string -> trimmed, de-duped, valid emails.
const parseEmails = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  const seen = new Set();
  return String(value)
    .split(",")
    .map((e) => e.trim())
    .filter((e) => e && isValidEmail(e) && !seen.has(e) && seen.add(e));
};

export const createSchedule = async ({
  userId,
  userEmail,
  userName,
  name,
  frequency,
  startAt,
  timezone,
  isActive = true,
  userRequest,
  toEmail,
  ccEmail,
  bccEmail,
}) => {
  const notification = { to_email: parseEmails(toEmail) };
  const cc = parseEmails(ccEmail);
  const bcc = parseEmails(bccEmail);
  if (cc.length) notification.cc_email = cc;
  if (bcc.length) notification.bcc_email = bcc;

  const body = {
    name,
    frequency,
    start_at: toApiDatetime(startAt),
    timezone,
    is_active: isActive,
    job_buffer_time: ATLAS_JOB_BUFFER_TIME,
    job_type: ATLAS_JOB_TYPE,
    payload: {
      user_details: {
        user_id: String(userId ?? ""),
        user_email: userEmail || "",
        user_name: userName || "",
      },
      user_request: userRequest || "",
      notification,
    },
  };

  const query = new URLSearchParams({
    user_id: String(userId ?? ""),
    user_email: userEmail || "",
  }).toString();

  const response = await axios({
    method: "POST",
    url: `${APISchedulingUrl()}/schedules?${query}`,
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": APIUrlToken(),
    },
    data: body,
  });
  return response.data;
};

/**
 * GET {REACT_APP_SCHEDULING_API_URL}/schedules?user_id=...
 * Returns the list of schedules for a user.
 */
export const getSchedules = async ({ userId }) => {
  const query = new URLSearchParams({
    user_id: String(userId ?? ""),
  }).toString();

  const response = await axios({
    method: "GET",
    url: `${APISchedulingUrl()}/schedules?${query}`,
    headers: {
      "X-API-Key": APIUrlToken(),
    },
  });
  return response.data;
};

/**
 * POST {REACT_APP_SCHEDULING_API_URL}/schedules/{schedule_id}/run-now
 * Triggers an immediate run of the given schedule.
 */
export const runScheduleNow = async ({ scheduleId }) => {
  const response = await axios({
    method: "POST",
    url: `${APISchedulingUrl()}/schedules/${scheduleId}/run-now`,
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": APIUrlToken(),
    },
  });
  return response.data;
};

/**
 * PATCH {REACT_APP_SCHEDULING_API_URL}/schedules/{schedule_id}
 * Updates the given schedule. `changes` must contain ONLY modified fields.
 */
export const updateSchedule = async ({ scheduleId, changes }) => {
  const response = await axios({
    method: "PATCH",
    url: `${APISchedulingUrl()}/schedules/${scheduleId}`,
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": APIUrlToken(),
    },
    data: changes,
  });
  return response.data;
};

/**
 * DELETE {REACT_APP_SCHEDULING_API_URL}/schedules/{schedule_id}?user_id=...
 * Deletes the given schedule.
 */
export const deleteSchedule = async ({ scheduleId, userId }) => {
  const query = new URLSearchParams({
    user_id: String(userId ?? ""),
  }).toString();

  const response = await axios({
    method: "DELETE",
    url: `${APISchedulingUrl()}/schedules/${scheduleId}?${query}`,
    headers: {
      "X-API-Key": APIUrlToken(),
    },
  });
  return response.data;
};
